/**
 * useFatigue — Muscle Fatigue Decay Hook
 *
 * Implements the exponential decay model:
 *   F(t) = F0 * e^(-k * t)
 *
 * Where:
 *   F0 = fatigueAtUpdate (stored in MuscleState at time of last set logged)
 *   k  = decayConstantPerHour (muscle-specific, stored in MuscleState)
 *   t  = hours elapsed since lastUpdatedAt
 *
 * F(t) range: 0.0 (fully recovered) → 1.0 (maximally fatigued)
 * Recovery % = (1 - F(t)) * 100
 *
 * The generator treats muscles with F(t) > 0.5 as unavailable.
 * Injured muscles are always treated as F(t) = 1.0.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import type { MuscleGroup, MuscleState } from '@/types';

// ── Pure Math ────────────────────────────────────────────────

/**
 * Computes current fatigue from a stored MuscleState record.
 * Always call this rather than reading fatigueAtUpdate directly.
 */
export function computeCurrentFatigue(state: MuscleState): number {
  if (state.isInjured) return 1.0;

  const now = Date.now();
  const lastUpdate = new Date(state.lastUpdatedAt).getTime();
  const hoursElapsed = (now - lastUpdate) / 3_600_000;

  const currentFatigue =
    state.fatigueAtUpdate * Math.exp(-state.decayConstantPerHour * hoursElapsed);

  // Clamp to [0, 1] to absorb floating-point drift
  return Math.max(0, Math.min(1, currentFatigue));
}

/** Recovery percentage: 0% = maximally fatigued, 100% = fully fresh */
export function computeRecoveryPercent(state: MuscleState): number {
  return Math.round((1 - computeCurrentFatigue(state)) * 100);
}

/**
 * Returns estimated hours until fatigue drops below a threshold.
 * Useful for "ready in ~X hours" UX copy.
 *   t_ready = -ln(threshold / F0) / k
 */
export function hoursUntilRecovery(
  state: MuscleState,
  threshold = 0.1,
): number {
  const current = computeCurrentFatigue(state);
  if (current <= threshold) return 0;
  if (state.decayConstantPerHour <= 0) return Infinity;
  return -Math.log(threshold / current) / state.decayConstantPerHour;
}

// ── Reactive Hooks ────────────────────────────────────────────

/** Live fatigue (0–1) for a single muscle, re-rendered on DB change. */
export function useMuscleFatigue(muscle: MuscleGroup): {
  fatigue: number;
  recoveryPercent: number;
  isInjured: boolean;
  hoursUntilReady: number;
  state: MuscleState | undefined;
} {
  const state = useLiveQuery(() => db.muscleStates.get(muscle), [muscle]);

  if (!state) {
    return { fatigue: 0, recoveryPercent: 100, isInjured: false, hoursUntilReady: 0, state: undefined };
  }

  const fatigue = computeCurrentFatigue(state);
  return {
    fatigue,
    recoveryPercent: Math.round((1 - fatigue) * 100),
    isInjured: state.isInjured,
    hoursUntilReady: hoursUntilRecovery(state),
    state,
  };
}

/** Live fatigue map for ALL muscles — used by the Dashboard. */
export function useAllMuscleFatigue(): Map<MuscleGroup, {
  fatigue: number;
  recoveryPercent: number;
  isInjured: boolean;
}> {
  const states = useLiveQuery(() => db.muscleStates.toArray(), []);

  const result = new Map<MuscleGroup, { fatigue: number; recoveryPercent: number; isInjured: boolean }>();
  for (const state of states ?? []) {
    const fatigue = computeCurrentFatigue(state);
    result.set(state.muscle, {
      fatigue,
      recoveryPercent: Math.round((1 - fatigue) * 100),
      isInjured: state.isInjured,
    });
  }
  return result;
}

/**
 * Returns which muscles are currently available for training:
 *   F(t) ≤ 0.5 AND not injured.
 */
export function useAvailableMuscles(): MuscleGroup[] {
  const states = useLiveQuery(() => db.muscleStates.toArray(), []);
  if (!states) return [];

  return states
    .filter((s) => !s.isInjured && computeCurrentFatigue(s) <= 0.5)
    .map((s) => s.muscle);
}

/**
 * Toggle injured status for a muscle.
 * Convenience action — wraps a direct DB write.
 */
export async function setMuscleInjured(
  muscle: MuscleGroup,
  injured: boolean,
): Promise<void> {
  await db.muscleStates.update(muscle, { isInjured: injured });
}
